import type { ModActionBody } from "@cfg/api-contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApp } from "../app-context.tsx";
import { useMe } from "../me.ts";
import { DefaultEmptyState } from "./defaults.tsx";

/** Staff-only queue of reported community entries. */
export function ModerationPage() {
  const { api, brand, copy } = useApp();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const me = useMe();
  const qc = useQueryClient();
  const isStaff = me.data?.plan === "staff";
  const queue = useQuery({ queryKey: ["modq", brand.config.id], queryFn: () => api!.modQueue(), enabled: !!api && isStaff });
  const [error, setError] = useState<string | null>(null);

  if (!api || (me.isFetched && !isStaff)) return <Empty title="Staff only" />;
  if (queue.isLoading || me.isLoading) return <p className="muted page">Loading…</p>;

  const act = async (id: string, action: ModActionBody["action"]) => {
    if (action === "remove" && !window.confirm("Remove this entry and make the owner's configuration private?")) return;
    setError(null);
    try {
      await api.modAction(id, { action });
      await qc.invalidateQueries({ queryKey: ["modq"] });
      await qc.invalidateQueries({ queryKey: ["community"] });
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const items = queue.data?.items ?? [];

  return (
    <section className="page page-mod">
      <header className="page-head">
        <h1>{copy("mod.title")}</h1>
      </header>
      {error && <p className="cfg-error">{error}</p>}
      {items.length === 0 ? (
        <Empty title={copy("mod.empty")} />
      ) : (
        <ul className="mine-list">
          {items.map((it) => (
            <li key={it.id} className="mine-item mod-item">
              <div>
                <strong>{it.name}</strong>{" "}
                <span className={`version-pill${it.status === "hidden" ? " is-warn" : ""}`}>{it.status}</span>
                <span className="muted small">
                  {" "}
                  {it.designId} · {copy("community.by", { name: it.authorName })} · {it.reportCount} report(s) since {new Date(it.firstReportAt).toLocaleString()}
                </span>
                <ul className="mod-reports">
                  {it.reports.map((r, i) => (
                    <li key={i} className="small">
                      <strong>{r.reason}</strong> {r.note && <span className="muted">“{r.note}”</span>}
                    </li>
                  ))}
                </ul>
                {it.status === "live" && (
                  <a className="small" href={`/community/${encodeURIComponent(it.id)}`}>
                    view
                  </a>
                )}
              </div>
              <div className="panel-save-row">
                {it.status === "live" ? (
                  <button type="button" className="cfg-chip" onClick={() => act(it.id, "hide")}>
                    Hide
                  </button>
                ) : (
                  <button type="button" className="cfg-chip" onClick={() => act(it.id, "restore")}>
                    Restore
                  </button>
                )}
                <button type="button" className="cfg-chip" onClick={() => act(it.id, "dismiss")} title="Clear the reports and keep it live">
                  Dismiss
                </button>
                <button type="button" className="cfg-chip" onClick={() => act(it.id, "remove")}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
